/**
 * volumes.js — closed-surface volume, and whether the surface is actually closed.
 *
 * Pure: no three.js, no DOM, no canvas. Takes raw typed arrays so it can be unit
 * tested on synthetic geometry without a GL context, and so the numbers it
 * produces cannot depend on a renderer.
 *
 * The orbital design-confirmation report quotes three volumes in cc. Those
 * figures used to be read off a CAD tool and typed into the document; this module
 * is what removes that transcription step. The maths is the same signed-tetrahedra
 * sum `computeMetrics()` in meshRenderer.js already uses — deliberately the same,
 * so the two cannot drift — but that function is not reusable here: it is not
 * exported, it sums volume across every mesh of a SCENE rather than per mesh, and
 * it never answers the watertight question (`watertight: null`, by design).
 *
 * ── Why welding is not optional ─────────────────────────────────────────────
 *
 * Measured on the delivered reference case (O3D-2026-06-18-0001): all three
 * orbital volume meshes report 0 boundary edges and 0 non-manifold edges on their
 * RAW indices. `PostPlanLeftOrbitalVolume.OBJ` is nonetheless not a closed
 * surface — the defect only appears once vertices at the same position are
 * merged, because the file carries duplicate vertices and doubled faces.
 *
 * So a watertightness check that skips welding passes the one mesh in the case
 * that needed the warning. That is the whole reason this module exists rather
 * than a two-line edge count.
 *
 * ── The tolerance, chosen by measurement and not by feel ────────────────────
 *
 * A LOOSER tolerance is the dangerous one here, which is the opposite of the
 * intuition. Merging vertices that were genuinely distinct fuses two surfaces
 * into a seam and invents non-manifold edges, so it reports a closed mesh as
 * open. Measured on the three reference meshes:
 *
 *   weld           NativeRight   NativeLeft        PostPlanLeft
 *   none           closed        closed            closed      <- hides the real defect
 *   exact (0)      closed        closed            NOT closed (3 nm edges)
 *   1e-6 mm grid   closed        closed            NOT closed (3)
 *   1e-5 mm grid   closed        closed            NOT closed (3)
 *   1e-4 mm grid   closed        NOT closed (3)    NOT closed (4)  <- two false seams
 *
 * The real defect in PostPlanLeft is **6 bit-identical duplicate vertices** —
 * measured separation exactly 0.000000000 mm — forming 3 non-manifold edges. It
 * is caught by an exact weld, and missed entirely without welding. What the
 * 1e-4 mm grid additionally merged were vertices ~6e-5 mm apart: genuinely
 * distinct points a 0.1 micron grid rounded together, inventing 3 non-manifold
 * edges on NativeLeft and a 4th on PostPlanLeft.
 *
 * So the default is an **exact** weld, where all three meshes agree with an
 * independent implementation (trimesh 4.4.0, whose own merge reaches the same
 * verdict and the same 3 edges) while the raw-index check still gets the answer
 * wrong on the one mesh that matters. See WELD_TOLERANCE_MM for why a grid
 * tolerance was rejected outright rather than merely tuned.
 *
 * ⚠️ Precision note. These numbers were established in float64. In the browser
 * three.js loads OBJ positions into Float32Array, whose spacing at a 100 mm
 * coordinate is ~7.6e-6 mm. Bit-identical duplicates stay bit-identical after
 * that narrowing, so the verdict holds; the ~6e-5 mm pairs stay distinct too
 * (about 8 representable steps apart). The e2e run over the real case asserts
 * the verdicts in the real float32 path, because this reasoning is not a
 * substitute for measuring it there.
 *
 * ── The volume is computed on the RAW geometry, not the welded copy ──────────
 *
 * The signed-tetrahedra sum is independent of how vertices are shared: merging
 * duplicates cannot change it. Keeping the two operations separate means a bug in
 * welding can only ever move the topology verdict, never a reported number.
 */

/**
 * Default vertex-merge tolerance, in millimetres: **0, meaning exact position**.
 *
 * Chosen over a grid tolerance after measuring what the defect actually is — see
 * the tolerance table in the header. Three reasons exact wins here:
 *
 *  1. The real defect is bit-identical duplicate vertices (measured separation
 *     exactly 0.000000000 mm), which exact matching catches by construction.
 *  2. The near-duplicates on the other two meshes (~6e-5 mm apart) are genuinely
 *     distinct and MUST NOT merge. Any tolerance large enough to be meaningful
 *     is large enough to fuse them and report a closed mesh as open.
 *  3. Grid quantisation is position-dependent: whether two points within the
 *     tolerance land in the same cell depends on where they sit relative to the
 *     grid, so a "tolerance" weld merges near-duplicates unpredictably. A
 *     clinical verdict should not vary with a mesh's absolute coordinates.
 *
 * A non-zero tolerance is still accepted by `weldPositions` for callers that
 * genuinely want grid snapping — with that position dependence understood.
 */
export const WELD_TOLERANCE_MM = 0

/**
 * Signed volume of a closed triangle mesh, in cubic centimetres.
 *
 * Sum of the signed volumes of the tetrahedra formed by each triangle and the
 * origin: `a · (b × c) / 6`. Absolute value, because winding decides the sign and
 * an inward-wound mesh is not a negative object.
 *
 * Returns 0 for geometry with no complete triangle rather than throwing — a
 * caller measuring a folder should not lose every other mesh's figure to one
 * empty file.
 *
 * @param {Float32Array|Float64Array|number[]} positions xyz triples, in mm
 * @param {Uint32Array|Uint16Array|number[]|null} [index] triangle indices, or null for a triangle soup
 * @returns {number} volume in cm³
 */
export function signedVolumeCc(positions, index = null) {
  const n = index ? index.length : positions.length / 3
  let mm3 = 0
  for (let i = 0; i + 2 < n; i += 3) {
    const ia = (index ? index[i] : i) * 3
    const ib = (index ? index[i + 1] : i + 1) * 3
    const ic = (index ? index[i + 2] : i + 2) * 3
    const ax = positions[ia], ay = positions[ia + 1], az = positions[ia + 2]
    const bx = positions[ib], by = positions[ib + 1], bz = positions[ib + 2]
    const cx = positions[ic], cy = positions[ic + 1], cz = positions[ic + 2]
    // a · (b × c)
    mm3 += (ax * (by * cz - bz * cy)
          + ay * (bz * cx - bx * cz)
          + az * (bx * cy - by * cx)) / 6
  }
  return Math.abs(mm3) / 1000        // mm³ → cm³ (= cc)
}

/**
 * Merge vertices that occupy the same position, and re-index onto them.
 *
 * With the default tolerance of 0, vertices merge when their coordinates are
 * exactly equal — deterministic, and independent of where the mesh sits in
 * space. A non-zero tolerance snaps coordinates to that grid first, which also
 * catches some near-duplicates but is position-dependent (see WELD_TOLERANCE_MM).
 *
 * Degenerate triangles produced by the merge (two or three corners now the same
 * vertex) are DROPPED — they carry no area, contribute nothing to the volume, and
 * would otherwise show up as spurious non-manifold edges.
 *
 * @returns {{positions: Float64Array, index: Uint32Array, droppedTris: number, mergedVerts: number}}
 */
export function weldPositions(positions, index = null, tolerance = WELD_TOLERANCE_MM) {
  const snap = tolerance > 0
  const inv = snap ? 1 / tolerance : 1
  const map = new Map()
  const out = []
  const remap = new Int32Array(positions.length / 3).fill(-1)

  const vertexFor = (vi) => {
    if (remap[vi] >= 0) return remap[vi]
    const p = vi * 3
    // `| 0` would overflow past ±2^31 on a fine grid; keep the key in float space.
    // `+0` normalises -0 to 0 so a mesh touching an axis cannot split a vertex.
    const key = snap
      ? `${Math.round(positions[p] * inv)},${Math.round(positions[p + 1] * inv)},`
        + `${Math.round(positions[p + 2] * inv)}`
      : `${positions[p] + 0},${positions[p + 1] + 0},${positions[p + 2] + 0}`
    let at = map.get(key)
    if (at === undefined) {
      at = out.length / 3
      map.set(key, at)
      out.push(positions[p], positions[p + 1], positions[p + 2])
    }
    remap[vi] = at
    return at
  }

  const n = index ? index.length : positions.length / 3
  const tris = []
  let droppedTris = 0
  for (let i = 0; i + 2 < n; i += 3) {
    const a = vertexFor(index ? index[i] : i)
    const b = vertexFor(index ? index[i + 1] : i + 1)
    const c = vertexFor(index ? index[i + 2] : i + 2)
    if (a === b || b === c || a === c) { droppedTris += 1; continue }
    tris.push(a, b, c)
  }

  const inVerts = positions.length / 3
  const outVerts = out.length / 3
  return {
    positions: Float64Array.from(out),
    index: Uint32Array.from(tris),
    droppedTris,
    mergedVerts: inVerts - outVerts,
  }
}

/**
 * Edge topology of an indexed mesh.
 *
 * A closed, manifold surface has every edge shared by exactly two triangles.
 * An edge used once is a boundary — a hole. An edge used more than twice is
 * non-manifold: surfaces meeting along a seam, which a volume is not.
 *
 * Edges are keyed unordered (`min,max`), so winding does not affect the count.
 *
 * @returns {{boundaryEdges:number, nonManifoldEdges:number, edges:number}}
 */
export function boundaryStats(positions, index) {
  if (!index || !index.length) return { boundaryEdges: 0, nonManifoldEdges: 0, edges: 0 }
  const counts = new Map()
  const bump = (u, v) => {
    const key = u < v ? `${u},${v}` : `${v},${u}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  for (let i = 0; i + 2 < index.length; i += 3) {
    const a = index[i], b = index[i + 1], c = index[i + 2]
    bump(a, b); bump(b, c); bump(c, a)
  }
  let boundaryEdges = 0, nonManifoldEdges = 0
  for (const used of counts.values()) {
    if (used === 1) boundaryEdges += 1
    else if (used > 2) nonManifoldEdges += 1
  }
  return { boundaryEdges, nonManifoldEdges, edges: counts.size }
}

/**
 * The one entry point callers use: volume, and whether to trust it as a volume.
 *
 * `watertight` is the verdict on the WELDED topology (see the header). `cc` is
 * computed on the geometry as supplied and is reported either way — the delivered
 * reference quoted a non-watertight mesh's volume, and refusing to would diverge
 * from the document this reproduces.
 *
 * @returns {{cc:number, watertight:boolean, boundaryEdges:number,
 *            nonManifoldEdges:number, tris:number, verts:number,
 *            mergedVerts:number, droppedTris:number}}
 */
export function analyzeGeometry(positions, index = null, tolerance = WELD_TOLERANCE_MM) {
  const cc = signedVolumeCc(positions, index)
  const welded = weldPositions(positions, index, tolerance)
  const { boundaryEdges, nonManifoldEdges } = boundaryStats(welded.positions, welded.index)
  return {
    cc,
    watertight: boundaryEdges === 0 && nonManifoldEdges === 0 && welded.index.length > 0,
    boundaryEdges,
    nonManifoldEdges,
    tris: welded.index.length / 3,
    verts: welded.positions.length / 3,
    mergedVerts: welded.mergedVerts,
    droppedTris: welded.droppedTris,
  }
}

/** Volume as the report prints it: two decimals and the unit, e.g. `29.24cc`. */
export const formatCc = (cc) => `${Number(cc).toFixed(2)}cc`

/**
 * The warning a non-watertight mesh earns, naming the file and the figure.
 *
 * Stated as a remedy, not a status: the operator's job is to confirm the number
 * before the document leaves the building.
 */
export const watertightWarning = (filename, cc) =>
  `${filename} is not a closed surface. Its volume (${formatCc(cc)}) is still reported, `
  + 'computed from signed volumes — confirm it before sending.'
